import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import { socialService } from '../services';
import toast from 'react-hot-toast';

const Leaderboard = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all_time');
  const [metric, setMetric] = useState('volume');

  useEffect(() => {
    loadLeaderboard();
  }, [period, metric]);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      const response = await socialService.getLeaderboard(period, metric);
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
      toast.error('Error al cargar el ranking');
    } finally {
      setLoading(false);
    }
  };

  const getMedalEmoji = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const formatValue = (value, metricType) => {
    if (metricType === 'volume') return `${Math.round(value).toLocaleString()} kg`;
    if (metricType === 'workouts') return `${value} entrenamientos`;
    if (metricType === 'records') return `${value} récords`;
    return value;
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-black text-gray-800 mb-2">🏆 Ranking Global</h1>
          <p className="text-gray-600">Compite con la comunidad CodiFit</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Period selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Período
              </label>
              <div className="flex gap-2">
                {[
                  { value: 'week', label: 'Semana' },
                  { value: 'month', label: 'Mes' },
                  { value: 'all_time', label: 'Todo' }
                ].map(p => (
                  <button
                    key={p.value}
                    onClick={() => setPeriod(p.value)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      period === p.value
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Metric selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Métrica
              </label>
              <div className="flex gap-2">
                {[
                  { value: 'volume', label: '💪 Volumen' },
                  { value: 'workouts', label: '🏋️ Entrenamientos' },
                  { value: 'records', label: '🏆 Récords' }
                ].map(m => (
                  <button
                    key={m.value}
                    onClick={() => setMetric(m.value)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      metric === m.value
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Leaderboard */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-4xl mb-2">🏆</p>
              <p>No hay datos disponibles</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {leaderboard.map((entry, index) => (
                <motion.div
                  key={entry.user.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`p-4 hover:bg-gray-50 transition-colors ${
                    entry.rank <= 3 ? 'bg-gradient-to-r from-yellow-50 to-transparent' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      {/* Rank */}
                      <div className={`text-2xl font-black ${
                        entry.rank === 1 ? 'text-yellow-500' :
                        entry.rank === 2 ? 'text-gray-400' :
                        entry.rank === 3 ? 'text-amber-600' :
                        'text-gray-600'
                      }`}>
                        {getMedalEmoji(entry.rank)}
                      </div>

                      {/* User info */}
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold">
                          {entry.user.avatar ? (
                            <img 
                              src={entry.user.avatar} 
                              alt={entry.user.name}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            entry.user.name.charAt(0).toUpperCase()
                          )}
                        </div>
                        <div>
                          <p className="font-bold text-gray-800">{entry.user.name}</p>
                          <p className="text-sm text-gray-500">
                            {formatValue(entry.value, metric)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Value bar */}
                    <div className="hidden md:block w-48">
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all"
                          style={{ 
                            width: `${Math.min((entry.value / leaderboard[0].value) * 100, 100)}%` 
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Privacy notice */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>💡 Solo aparecen usuarios con perfil público</p>
          <p className="mt-1">
            Configura tu privacidad en{' '}
            <span className="text-blue-500 font-medium cursor-pointer hover:underline">
              Ajustes
            </span>
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default Leaderboard;
